import { assertUnreachable } from '@sgme/fp';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

import { InfoPill } from '@/App/tiles/Option/common/InfoPill.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { NotionalHeader } from '@/App/tiles/Option/strategy/fieldHeaders/NotionalHeader.tsx';
import { PinAllPremiums } from '@/App/tiles/Option/strategy/PinAllPremiums.tsx';
import type { StrategyField } from '@/App/tiles/Option/strategy/types.tsx';
import { selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';
import type { LegPropertiesSliceWithId } from '@/store/option/state.ts';

interface FieldHeaderProps {
  field: StrategyField;
  tileId: string;
}

export function FieldHeader(props: FieldHeaderProps) {
  const isExpired = useOptionSelector((option) => option.properties.rfs.status === 'EXPIRED');
  return (
    <div
      className={clsx(
        'header d-flex flex-center justify-content-between flex-row-reverse col-form-label col-form-label-sm px-1',
        { expired: isExpired },
      )}
    >
      <FieldHeaderComponent {...props} />
    </div>
  );
}

function FieldHeaderComponent(props: FieldHeaderProps) {
  const { currency1, currency2 } = useOptionSelector(selectOptionCurrency);
  const [checkOption] = useCheckOption();

  const legs = useOptionSelector((option) => option.properties.legs);

  switch (props.field) {
    case 'side':
      return (
        <>
          <FormattedMessage id="option.fields.side" />
          <button
            type="button"
            className="btn btn-sm btn-flat-secondary btn-icon-start px-2 my-n1"
            data-e2e="ToggleAllLegsWay-btn"
            onClick={() =>
              checkOption({
                tileId: props.tileId,
                legProperties: legs.map(
                  (leg) =>
                    ({ legId: leg.legId, side: leg.side === 'Buy' ? 'Sell' : 'Buy' }) as LegPropertiesSliceWithId,
                ),
              })
            }
          >
            <i className="icon line-height-1">swap_horiz</i>
          </button>
        </>
      );
    case 'type':
      return <FormattedMessage id="option.fields.type" values={{ inStrategyPanel: true }} tagName="div" />;
    case 'expiry':
      return <FormattedMessage id="option.fields.expiry" />;
    case 'strike':
      return (
        <>
          <FormattedMessage id="option.fields.strike" />
          <InfoPill messageId="option.fields.strike.tooltip" />
        </>
      );
    case 'notional':
      return <NotionalHeader tileId={props.tileId} />;
    case 'delivery':
      return <FormattedMessage id="option.fields.delivery" />;
    case 'triggerType':
      return <FormattedMessage id="option.fields.triggerType" />;
    case 'firstTrigger1':
      return <FormattedMessage id="option.fields.trigger" values={{ number: 1 }} />;
    case 'secondTrigger1':
      return '';
    case 'TriggerStartDate1':
      return <FormattedMessage id="option.fields.triggerStartDate" />;
    case 'TriggerEndDate1':
      return <FormattedMessage id="option.fields.triggerEndDate" />;
    case 'firstTrigger2':
      return <FormattedMessage id="option.fields.trigger" values={{ number: 2 }} />;
    case 'secondTrigger2':
      return '';
    case 'TriggerStartDate2':
      return <FormattedMessage id="option.fields.triggerStartDate" />;
    case 'TriggerEndDate2':
      return <FormattedMessage id="option.fields.triggerEndDate" />;
    case 'premium':
      return (
        <>
          <span className="fw-bolder">
            <FormattedMessage id="option.fields.premium" />
          </span>
          <PinAllPremiums />
        </>
      );
    case 'premiumDate':
      return (
        <span className="fw-bolder">
          <FormattedMessage id="option.fields.premiumDate" />
        </span>
      );
    case 'markup':
      return (
        <span className="fw-bolder">
          <FormattedMessage id="option.fields.markup" />
        </span>
      );
    case 'volatility':
      return (
        <span className="fw-bolder">
          <FormattedMessage id="option.fields.volatility" />
        </span>
      );
    case 'atmVolatility':
      return <FormattedMessage id="option.fields.atomVolatility" />;
    case 'forward':
      return <FormattedMessage id="option.fields.forward" />;
    case 'hedgeRate':
      return <FormattedMessage id="option.fields.hedgeRate" />;
    case 'hedgeAmount':
      return <FormattedMessage id="option.fields.hedgeAmount" />;
    case 'hedgeDate':
      return <FormattedMessage id="option.fields.hedgeDate.inStrategyPanel" />;
    case 'swap':
      return <FormattedMessage id="option.fields.swapPoints" />;
    case 'zc1':
      return <FormattedMessage id="option.fields.zc<1|2>" values={{ currency: currency1 }} />;
    case 'zc2':
      return <FormattedMessage id="option.fields.zc<1|2>" values={{ currency: currency2 }} />;
    case 'delta':
      // always display "delta" in strategy panel
      return <FormattedMessage id="option.fields.greeks.delta" values={{ hedged: false }} />;
    case 'vega':
      return <FormattedMessage id="option.fields.greeks.vega" />;
    case 'gamma':
      return <FormattedMessage id="option.fields.greeks.gamma" />;
    case 'theta':
      return <FormattedMessage id="option.fields.greeks.theta" />;
    default:
      assertUnreachable(props.field, '');
  }
}
